import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ClipboardList, Mail, ArrowDownToLine, Zap, AlertTriangle } from 'lucide-react';
import { safeFetch } from '../../../lib/api';
import { notifyToast } from '../../../lib/toastStore';
import { confirmDialog } from '../../../lib/confirmStore';

interface AdminAction {
  key: string;
  label: string;
  why: string;
  kind: 'email' | 'drawer' | 'action';
  actionApi?: string;
  uid: string;
  displayName: string;
  applicationId: string;
  program: string;
  stage: string;
  stageLabel: string;
}

interface ApplicantAdminActionsProps {
  uid: string;
  /**
   * Freshness signal from the drawer (PR-20): bumped on every
   * successful drawer (re)load. When it changes after mount, the
   * action list refetches so it can't go stale behind the record —
   * the actions are derived from the applicant's CURRENT stage.
   */
  refreshSignal?: number;
  /** Open the AI draft-email modal for this applicant. */
  onDraftEmail: () => void;
  /** Scroll the drawer to the stage-focus section (for 'drawer'-kind actions). */
  onGoToFocus: () => void;
  /** Refresh parent state after a one-click action runs. */
  onChanged: () => void;
}

const KIND_ICON: Record<string, React.ReactNode> = {
  email: <Mail className="h-3.5 w-3.5" />,
  drawer: <ArrowDownToLine className="h-3.5 w-3.5" />,
  action: <Zap className="h-3.5 w-3.5" />,
};

const KIND_CTA: Record<string, string> = {
  email: 'Draft email',
  drawer: 'Go to section',
  action: 'Run now',
};

/**
 * Admin Actions — integrated into the applicant drawer's slide-in modal
 * (moved off the Workflow page 2026-10-11 to declutter it).
 *
 * Shows the computed actions for THIS applicant's current stage only,
 * reusing the server derivation (GET /api/admin/workflow/admin-actions?uid=).
 * Each action keeps its one-click behavior.
 */
export const ApplicantAdminActions: React.FC<ApplicantAdminActionsProps> = ({
  uid,
  refreshSignal,
  onDraftEmail,
  onGoToFocus,
  onChanged,
}) => {
  const [actions, setActions] = useState<AdminAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingKey, setActingKey] = useState<string | null>(null);

  const fetchActions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data: any = await safeFetch(`/api/admin/workflow/admin-actions?uid=${encodeURIComponent(uid)}`);
      setActions(data.actions || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load admin actions');
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  // Refetch when the drawer reloads (external change, post-action
  // refresh) — the mount fetch above covers the initial value only.
  const lastSignalRef = useRef(refreshSignal ?? 0);
  useEffect(() => {
    const s = refreshSignal ?? 0;
    if (s !== lastSignalRef.current) {
      lastSignalRef.current = s;
      fetchActions();
    }
  }, [refreshSignal, fetchActions]);

  const runAction = async (a: AdminAction) => {
    if (a.kind === 'email') {
      onDraftEmail();
      return;
    }
    if (a.kind === 'drawer') {
      onGoToFocus();
      return;
    }
    // kind === 'action': one-click API (e.g. archive)
    if (!a.actionApi) return;
    if (!(await confirmDialog(`${a.label} for ${a.displayName} (#${a.applicationId})?`))) return;
    const rowKey = `${a.uid}:${a.key}`;
    setActingKey(rowKey);
    try {
      await safeFetch(a.actionApi, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicationIds: [a.applicationId] }),
      });
      notifyToast('success', `${a.label} done`);
      onChanged();
      fetchActions();
    } catch (err: any) {
      notifyToast('error', err.message || 'Action failed');
    } finally {
      setActingKey(null);
    }
  };

  if (loading && actions.length === 0) {
    return (
      <div className="bg-white border border-slate-200/80 rounded-2xl p-4 shadow-2xs">
        <div className="h-10 bg-slate-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-slate-200/80 rounded-2xl p-4 shadow-2xs flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-red-400 shrink-0" />
        <p className="text-xs font-bold text-slate-600 flex-1">{error}</p>
        <button type="button" onClick={fetchActions} className="text-xs font-bold text-indigo-600 underline cursor-pointer">
          Retry
        </button>
      </div>
    );
  }

  if (actions.length === 0) return null;

  return (
    <div className="bg-white border border-slate-200/80 rounded-2xl shadow-2xs overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
        <ClipboardList className="h-4 w-4 text-indigo-600" />
        <h3 className="text-xs font-extrabold uppercase tracking-wide text-slate-500">
          Admin Actions{' '}
          <span className="text-slate-400 tabular-nums">({actions.length})</span>
        </h3>
      </div>
      <ul className="divide-y divide-slate-100">
        {actions.map((a) => {
          const rowKey = `${a.uid}:${a.key}`;
          const busy = actingKey === rowKey;
          return (
            <li key={rowKey} className="flex items-center gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-slate-600">
                  <span className="font-bold text-slate-800">{a.label}</span>
                  <span className="text-slate-400"> — </span>
                  {a.why}
                </p>
              </div>
              <button
                type="button"
                onClick={() => runAction(a)}
                disabled={busy}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 min-h-[40px] rounded-xl bg-brand-primary hover:bg-brand-primary-dark disabled:opacity-50 text-white text-[11px] font-bold cursor-pointer"
              >
                {KIND_ICON[a.kind]}
                {busy ? 'Working…' : KIND_CTA[a.kind]}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
